import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";
import { useParams } from "react-router-dom";

export default function useStationBasicDetails() {
    const { stationId } = useParams();
    const STATION_DETAIL_CACHE_KEY = `station_detail_cache_${stationId}`;

    const [stationDetail, setStationDetail] = useState(() => {
        try {
            const cached = localStorage.getItem(STATION_DETAIL_CACHE_KEY);
            return cached ? JSON.parse(cached) : null;
        } catch (e) {
            console.warn("Failed to parse cached station detail", e);
            localStorage.removeItem(STATION_DETAIL_CACHE_KEY);
            return null;
        }
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const { send, isConnected, isOpen, connect, registerHandler } = useWebSocketContext();
    const mountedRef = useRef(true);
    const retryTimeoutRef = useRef(null);

    const persistStationDetail = useCallback((data) => {
        setStationDetail(data);
        try {
            localStorage.setItem(STATION_DETAIL_CACHE_KEY, JSON.stringify(data));
        } catch (e) {
            console.warn("Failed to persist station detail in localStorage", e);
        }
    }, [STATION_DETAIL_CACHE_KEY]);

    const waitForConnection = useCallback(() => {
        return new Promise((resolve, reject) => {
            const start = Date.now();
            const interval = setInterval(() => {
                if (!mountedRef.current) {
                    clearInterval(interval);
                    reject(new Error('Component unmounted'));
                } else if (isOpen()) {
                    clearInterval(interval);
                    resolve();
                } else if (Date.now() - start > 5000) {
                    clearInterval(interval);
                    reject(new Error('Connection timeout'));
                }
            }, 100);
        });
    }, [isOpen]);

    const fetchEachStationDetail = useCallback(async (attempt = 0) => {
        if (!mountedRef.current) return;
        setIsLoading(true);
        setError('');

        try {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }

            if (!isConnected) {
                try {
                    await connect();
                    await waitForConnection();
                } catch (err) {
                    console.warn("[WS] Connection failed, keeping old data:", err);
                    setIsLoading(false);
                    return; 
                }
            }

            const response = await send('cms_get_charging_station_details_by_station_id', {
                station_id: stationId
            });

            if (!response || response.type !== 'cms_get_charging_station_details_by_station_id_response') {
                throw new Error('Invalid response from server');
            }
            console.log("Response of station detail is", response.data)
            persistStationDetail(response.data);

        } catch (err) {
            console.error("[WS] Error fetching station detail:", err);
            setError(err.message);

            if (mountedRef.current && attempt < 5) { // max 5 retries
                const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
                retryTimeoutRef.current = setTimeout(() => {
                    fetchEachStationDetail(attempt + 1);
                }, delay);
            }
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, [send, connect, persistStationDetail, stationId, isConnected, waitForConnection]);

    useEffect(() => {
        mountedRef.current = true;

        // Register live update
        const unregister = registerHandler('station_update', (msg) => {
            if (msg.data && mountedRef.current) {
                persistStationDetail(msg.data);
            }
        });

        fetchEachStationDetail();

        return () => {
            mountedRef.current = false;
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
            unregister();
        };
    }, [fetchEachStationDetail, registerHandler, persistStationDetail]);

    return {
        stationDetail,
        isLoading,
        error,
        isConnected,
        refetch: () => fetchEachStationDetail(0)
    };
}
